import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import {
  PAYMENT_BEHAVIOUR,
  REVALIDATE_MODES,
  SPECIFIC_ERROR_CODES,
} from '@savgroup-front-common/constants';
import { formatPhoneNumber } from '@savgroup-front-common/core/formatters';
import { useSearchParams } from '@savgroup-front-common/core/hooks';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { PaymentProvider } from '@savgroup-front-common/types/Payment/PaymentProvider';
import { ClaimService } from '@/api';
import { ClaimGroupConfirmation } from '@/api/Claim/getClaimGroupConfirmationQuery';
import { useGetSellerConfiguration } from '@/view/app/hooks';
import useGetOrigin from '@/view/app/hooks/useGetOrigin';
import { useCreateInvoiceWithClaimGroup } from '@/view/app/hooks/useCreateInvoiceWithClaimGroup';
import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';
import { useLayout } from '@/view/app/NewLayout/NewLayout.hooks';

import claimGroupConfirmationSchema from './NewClaimGroupConfirmationPage.schema';
import { ClaimGroupConfirmationValues } from './NewClaimGroupConfirmationPage.types';
import {
  RoutedStepProps
} from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import { IrshStepValues } from '../IrshPages.types';
import {
  useRoutedStepsOrchestratorContext
} from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { InitContext } from '../../../app/NewLayout/InitProvider/InitProvider.context';
import useGetClaimGroupSummary, { USE_GET_CLAIMS_GROUP_SUMMARY } from '../hooks/useGetClaimGroupSummary';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { adaptVisitorData } from './adapters/adaptVisitorData';
import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { useGetPaymentAccount } from '../../../components/Payments/hooks/useGetPaymentAccount';

interface Args {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useClaimGroupConfirmationPage = ({onNextStep}: Args) => {
  const query = useSearchParams();
  const { origin } = useGetOrigin();
  const queryClient = useQueryClient();

  const paymentConfirmed = query.has('paymentConfirmed') ;
  const hasPaylineToken = query.has('paylinetoken');
  const hasPurseRedirectionData = query.has('purse-redirection-data');

  const { orders } = useContext(InitContext);

  const { pushErrors } = useToasts();
  const { login } = useLayout();

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>()

  const claimGroupId = values?.claimGroupId;

  const { claimGroupConfirmation, isLoading: isGetConfirmationLoading } =
    useGetClaimGroupConfirmation({
      claimGroupId,
    });

  const { paymentAccountDto } = useGetPaymentAccount({
    claimGroupId: claimGroupId ?? '',
    isEnabled: Boolean(claimGroupConfirmation && claimGroupConfirmation?.paymentBehavior !== PAYMENT_BEHAVIOUR.NO_PAYMENT),
  });

  const { claims } = useGetClaimGroupSummary({
    claimGroupId,
    suspense: true,
  });

  const firstClaim = claims?.at(0);

  const order = orders?.find(
    (order) => firstClaim && order.orderId === firstClaim.orderId,
  );

  const sellerId = order?.sellerId;

  const { sellerConfiguration, isLoading: isGetSellerConfigurationLoading } =
    useGetSellerConfiguration({ sellerId });

  const { errors, isLoading: isCheckInvoiceLoading } =
    useCreateInvoiceWithClaimGroup({ claimGroupId });

  const isInvoiceAlreadyPaid = errors?.some(
    (error) => error.code === SPECIFIC_ERROR_CODES.INVOICE_ALREADY_PAID,
  );

  const agreementUrl =
    sellerConfiguration?.cgvUrl ||
    `${APIConfiguration.catalogCDN}miscellaneous/CGU%20Revers.io%20-%20Client%20final_fr.pdf`;

  const paymentRequired =
    !isInvoiceAlreadyPaid &&
    claimGroupConfirmation?.paymentBehavior ===
      PAYMENT_BEHAVIOUR.PAYMENT_REQUIRED;

  const isMonextPayment =
    paymentAccountDto?.paymentProvider === PaymentProvider.MONEXT && paymentRequired;

  const formContext = useForm<ClaimGroupConfirmationValues>({
    resolver: yupResolver(claimGroupConfirmationSchema),
    mode: REVALIDATE_MODES.ON_CHANGE,
    defaultValues: {
      phone:
        formatPhoneNumber(
          login.phone || login?.ownerAddress?.phone,
          login.countryCode,
        ) || '',
      email: login.email,
      cgv: paymentConfirmed || hasPaylineToken || hasPurseRedirectionData,
      phoneCountryCode: login.countryCode,
    },
  });



  const {
    handleSubmit,
    trigger
  } = formContext;

  useEffect(() => {
    trigger();
  }, [ trigger]);

  const {
    mutateAsync: handleConfirmationSubmit,
    isLoading: isLoadingConformationSubmit,
  } = useMutation(
    ['setConfirmationSubmit'],
    async ({
      claimGroupId,
      payload,
    }: {
      claimGroupId?: string;
      payload: {
        phoneNumber?: string;
        mail?: string;
        swornStatement?: boolean;
        generalSalesCondition?: boolean;
        iMEI?: string;
        devicePassword?: string;
        serialNumber?: string;
        invoiceId?: string;
        sourceTokenId?: string;
        issueDate?: Date;
        origin?: string;
        ticketId?: string;
      };
    }) => {
      if (!claimGroupId) {
        return undefined;
      }

      const responseConfirmation = await ClaimService.setClaimConfirmation({
        claimGroupId,
        payload,
      });

      if (responseConfirmation.failure) {
        pushErrors(responseConfirmation.errors);

        return undefined;
      }

      return responseConfirmation;
    },
  );

  const visitorData = useVisitorData(
    { extendedResult: true },
    { immediate: true },
  );


  const onSubmit = handleSubmit(async ({ email, phone }) => {
    if (claimGroupId) {
      const payload = {
        phoneNumber: phone,
        mail: email,
        swornStatement: undefined,
        generalSalesCondition: true,
        iMEI: '',
        serialNumber: '',
        devicePassword: '',
        invoiceId: undefined,
        sourceTokenId: '',
        issueDate: new Date(),
        origin,
        ticketId: undefined,
      };

      const responseSetConfirmation = await handleConfirmationSubmit({
        claimGroupId,
        payload,
      });

      if (!responseSetConfirmation) {
        return undefined;
      }

      const responseGetConfirmation =
        await ClaimService.getClaimGroupConfirmationQuery({
          claimGroupId,
        });

      if (responseGetConfirmation.failure) {
        return undefined;
      }

      if (visitorData?.data) {
        const adaptedVisitorData = adaptVisitorData(visitorData);
        const responseSetUserMetadata = await ClaimService.setUserMetadataCommand({
          claimGroupId,
          visitorId: adaptedVisitorData.visitorId,
          visitorData: adaptedVisitorData.data,
        });

        if (responseSetUserMetadata.failure) {
          logError(responseSetUserMetadata.errors)

          return undefined;
        }
      } else {
        logError('No visitor data found')
      }

      const { fileId } = <ClaimGroupConfirmation>responseGetConfirmation.value;

      await queryClient.invalidateQueries([
        USE_GET_CLAIMS_GROUP_SUMMARY,
        { claimGroupId },
      ]);

      onNextStep({
        newValues: {
          fileId,
        }
      })

      return undefined;
    }

    return undefined;
  });

  useEffect(() => {
    if (paymentConfirmed) {
      onSubmit();
    }
  }, [paymentConfirmed, onSubmit]);

  const isLoading =
    isCheckInvoiceLoading ||
    isGetConfirmationLoading ||
    isGetSellerConfigurationLoading;

  return {
    claimGroupConfirmation,
    claimGroupId,
    formContext,
    onSubmit,
    agreementUrl,
    isInvoiceAlreadyPaid,
    isLoading,
    paymentRequired,
    sellerId,
    isLoadingSubmit: isLoadingConformationSubmit,
    isCheckInvoiceLoading,
    isMonextPayment,
  };
};

export default useClaimGroupConfirmationPage;
